import Alert from "@mui/material/Alert"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"

import DialogProvider from "@components/dialog_provider"
import { formatBytes } from "@utils/functions/format_bytes"

/**
 * Last gate before the bundle that every customer website executes is replaced.
 * The list of keys the orphan sweep will delete is the part worth reading — it
 * is the only place a wrong-folder selection still shows up as damage.
 */
export default function ConfirmPublishDialog({
  isOpen,
  setClose,
  translate,
  loading,
  fileCount,
  totalBytes,
  removedKeys,
  onConfirm
}: {
  isOpen: boolean
  setClose: () => void
  translate: (key: string) => string
  loading: boolean
  fileCount: number
  totalBytes: number
  removedKeys: string[]
  onConfirm: () => void
}) {
  return (
    <DialogProvider
      title={translate("live_chat_widget_confirm_title")}
      isOpen={isOpen}
      setClose={setClose}
      size="medium"
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: setClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("live_chat_widget_confirm_action"),
        color: "error",
        onClick: onConfirm,
        loading,
        disabled: loading
      }}
    >
      <Stack gap={2}>
        <Alert severity="warning">
          {translate("live_chat_widget_confirm_body")}
        </Alert>

        <Typography variant="body2">
          {translate("live_chat_widget_confirm_summary")
            .replace("{count}", String(fileCount))
            .replace("{size}", formatBytes(totalBytes))}
        </Typography>

        {removedKeys.length > 0 && (
          <Stack gap={0.5}>
            <Typography variant="body2" color="error">
              {translate("live_chat_widget_confirm_removed").replace(
                "{count}",
                String(removedKeys.length)
              )}
            </Typography>
            <Stack
              sx={{
                maxHeight: "10rem",
                overflowY: "auto",
                bgcolor: "action.hover",
                borderRadius: 1,
                p: 1
              }}
            >
              {removedKeys.map((key) => (
                <Typography
                  key={key}
                  variant="caption"
                  sx={{ fontFamily: "monospace" }}
                >
                  {key}
                </Typography>
              ))}
            </Stack>
          </Stack>
        )}

        <Typography variant="caption" color="text.secondary">
          {translate("live_chat_widget_confirm_order_hint")}
        </Typography>
      </Stack>
    </DialogProvider>
  )
}
